/**
 * Input schemas shared by the server and the web app. The server validates
 * every request body with these; the web app uses them for its forms.
 */
import type { RuleBody } from '@kept/schedule';
import { z } from 'zod';

export const isoDate = z.iso.date();
export const wallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM, 24-hour');
export const wallDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/, 'Use YYYY-MM-DDTHH:MM');

export const timeZone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Unknown time zone');

const partyId = z.uuid();
export const partyRef = z.union([partyId, z.object({ even: partyId, odd: partyId })]);
const monthDay = { month: z.int().min(1).max(12), day: z.int().min(1).max(31) };
const fixedPoint = z.object({ ...monthDay, time: wallTime });

export const ruleBody = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rotation'),
    anchor: isoDate,
    cycleDays: z.int().min(1).max(366),
    segments: z
      .array(
        z.object({
          startDay: z.int().min(-366).max(366),
          startTime: wallTime,
          endDay: z.int().min(-366).max(366),
          endTime: wallTime,
          party: partyRef,
        }),
      )
      .min(1),
  }),
  z.object({
    type: z.literal('midweek'),
    anchor: isoDate,
    everyWeeks: z.int().min(1).max(52),
    startTime: wallTime,
    endTime: wallTime,
    party: partyRef,
  }),
  z.object({
    type: z.literal('block'),
    label: z.string().min(1).max(100),
    start: wallDateTime,
    end: wallDateTime,
    party: partyRef,
  }),
  z.object({
    type: z.literal('schoolBreak'),
    breakKind: z.string().min(1).max(50),
    from: z.union([z.literal('release'), fixedPoint]),
    to: z.union([z.literal('return'), fixedPoint]),
    releaseTime: wallTime,
    returnTime: wallTime,
    party: partyRef,
    parityYear: z.enum(['start', 'end']).optional(),
  }),
  z.object({
    type: z.literal('specialDay'),
    day: z.union([z.enum(['mothersDay', 'fathersDay']), z.object(monthDay)]),
    startTime: wallTime,
    endTime: wallTime,
    party: partyRef,
  }),
  z.object({
    type: z.literal('holiday'),
    holiday: z.enum([
      'newYearsDay',
      'mlkDay',
      'presidentsDay',
      'easter',
      'memorialDay',
      'juneteenth',
      'independenceDay',
      'laborDay',
      'columbusDay',
      'veteransDay',
      'thanksgiving',
      'christmasEve',
      'christmasDay',
      'newYearsEve',
    ]),
    startOffsetDays: z.int().min(-14).max(14),
    startTime: wallTime,
    endOffsetDays: z.int().min(-14).max(14),
    endTime: wallTime,
    party: partyRef,
    includeAdjacentWeekend: z.boolean().optional(),
  }),
]);

// Compile-time check: a validated rule body is exactly what the engine accepts.
const _sameShape: (x: z.infer<typeof ruleBody>) => RuleBody = (x) => x;
void _sameShape;

export const createPlan = z
  .object({
    name: z.string().min(1).max(100),
    timeZone,
    child: z.object({ name: z.string().min(1).max(100), birthDate: isoDate.optional() }),
    parties: z
      .array(z.object({ label: z.string().min(1).max(100), role: z.enum(['mother', 'father', 'other']) }))
      .min(2)
      .max(6),
    defaultParty: z.int().min(0),
    measuredParty: z.int().min(0),
    statedShares: z.array(z.number().min(0).max(100)).optional(),
    exchangeLocation: z.string().max(300).optional(),
  })
  .refine((p) => p.defaultParty < p.parties.length && p.measuredParty < p.parties.length, {
    message: 'defaultParty and measuredParty must point at a listed party',
  })
  .refine((p) => p.defaultParty !== p.measuredParty, {
    message: 'The measured party cannot be the default party',
  })
  .refine((p) => !p.statedShares || p.statedShares.length === p.parties.length, {
    message: 'Give one stated share per party',
  });

export const updatePlan = z
  .object({
    name: z.string().min(1).max(100),
    timeZone,
    defaultPartyId: partyId,
    measuredPartyId: partyId,
    statedShares: z.record(z.uuid(), z.number().min(0).max(100)).nullable(),
    exchangeLocation: z.string().max(300).nullable(),
  })
  .partial();

export const updateParty = z
  .object({ label: z.string().min(1).max(100), role: z.enum(['mother', 'father', 'other']) })
  .partial();

export const updateChild = z
  .object({ name: z.string().min(1).max(100), birthDate: isoDate.nullable() })
  .partial();

const versionCommon = {
  effectiveFrom: isoDate,
  /** Why this version exists, e.g. "Child enrolled in school". */
  reason: z.string().max(500).optional(),
};

export const createRule = z.object({
  ...versionCommon,
  clause: z.string().min(1).max(100),
  tier: z.int().min(0).max(1000).optional(),
  rule: ruleBody,
});

export const createRuleVersion = z.union([
  createRule,
  z.object({ ...versionCommon, ended: z.literal(true) }),
]);

export const policySource = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('plan'), clause: z.string().min(1).max(100) }),
  z.object({ kind: z.literal('agreed'), note: z.string().min(1).max(500) }),
]);

export const policyBody = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('rightOfFirstRefusal'),
    threshold: z.union([z.literal('overnight'), z.object({ hours: z.number().positive().max(240) })]),
  }),
  z.object({
    kind: z.literal('gracePeriod'),
    pickupMinutes: z.int().min(0).max(240),
    returnMinutes: z.int().min(0).max(240),
    consequence: z.string().max(500).optional(),
  }),
  z.object({ kind: z.literal('notice'), hours: z.number().positive().max(24 * 60) }),
]);

export const createPolicy = z.object({ ...versionCommon, source: policySource, policy: policyBody });

export const createPolicyVersion = z.union([
  createPolicy,
  z.object({ ...versionCommon, ended: z.literal(true) }),
]);

export const createSchoolBreak = z
  .object({ kind: z.string().min(1).max(50), lastSchoolDay: isoDate, firstDayBack: isoDate })
  .refine((b) => b.firstDayBack > b.lastSchoolDay, { message: 'firstDayBack must be after lastSchoolDay' });

export const expandQuery = z
  .object({ from: isoDate, to: isoDate })
  .refine((q) => q.to >= q.from, { message: 'to must not be before from' })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 3 * 366 * 864e5, {
    message: 'Expand at most three years at a time',
  });

export type CreatePlan = z.infer<typeof createPlan>;
export type CreateRule = z.infer<typeof createRule>;
export type CreatePolicy = z.infer<typeof createPolicy>;
export type PolicyBody = z.infer<typeof policyBody>;
export type PolicySource = z.infer<typeof policySource>;
